import remote from "@/data/remote";
import {
	formatProductName,
	getMeasurements,
	formatDimensions,
	getAttributes,
	loadStockBundle,
} from "@/utils/stocksHelper.js";
import { getId, round } from "@/utils/commonHelper.js";

async function fetchAttrDefs(productId) {
	return new Promise((res) =>
		remote.productAttributes.getByProduct(productId, {
			onSuccess: (r) => res(r.items || []),
		})
	);
}

const prodCache = new Map();
async function loadProduct(id) {
	if (prodCache.has(id)) return prodCache.get(id);
	const p = await new Promise((res, rej) => remote.products.find(id, { onSuccess: res, onFail: rej }));
	prodCache.set(id, p);
	return p;
}

async function formatLineDimensions(line, p, bundles, batches, unitOptions) {
	if (!line.manual && line.stockId) {
		const batch = batches[getId(line.batchId)] || {};
		const overrides = (batch.measurements || [])
			.filter((m) => String(getId(m._id)) === String(getId(line.batchMeasurementId)))
			.flatMap((m) =>
				["length", "width", "height", "value"].filter((k) => m[k] != null).map((k) => ({ key: k, value: m[k] }))
			);
		if (overrides.length) {
			const hasValue = overrides.some((d) => d.key === "value");
			if (hasValue) return overrides.map((d) => `${d.value}${p.unitCode || ""}`).join(", ");
			const measurementDef = bundles[getId(line.stockId)].variant.measurements.find(
				(m) => String(getId(m._id)) === String(getId(line.measurementId))
			);
			const unitLabel = measurementDef?.unit || "";
			return `${formatDimensions(overrides)}${unitLabel ? `${unitLabel}` : ""}`.trim();
		}
		const b = bundles[getId(line.stockId)];
		const dims = b
			? getMeasurements({ unit: b.stock.product?.unit || b.stock.unit }, b.measurement, unitOptions).filter(
					(m) => m.key !== "pieces"
			  )
			: [];
		const measDef = b.variant.measurements.find((m) => String(getId(m._id)) === String(getId(line.measurementId)));
		const unitLabel = measDef?.unit || "";
		return `${formatDimensions(dims)}${unitLabel ? `${unitLabel}` : ""}`.trim();
	}
	const dims = getMeasurements({}, line, unitOptions).filter((m) => m.key !== "pieces");
	const product = await loadProduct(getId(p.productId));
	const variant = (product.variants || []).find((v) => String(getId(v._id)) === String(getId(p.variantId))) || {};
	const measDef = variant.measurements.find((m) => String(getId(m._id)) === String(getId(line.measurementId)));
	const unitLabel = measDef?.unit || p.unitCode || "";
	return `${formatDimensions(dims)}${unitLabel ? `${unitLabel}` : ""}`.trim();
}

export async function loadProformaProducts(proforma, vm) {
	const lang = vm.$lang || vm.$i18n.locale;
	const currencyCode = proforma.currency.code;
	const nf = new Intl.NumberFormat(lang, { style: "currency", currency: currencyCode });
	const flat = (proforma.products || []).flat().map((p) => ({
		...p,
		quantity: round(
			p.lines.reduce((s, l) => s + (l.quantity || 0), 0),
			4
		),
	}));

	const stockIds = Array.from(
		new Set(
			flat.flatMap((p) => p.lines.filter((l) => !l.manual && l.stockId).map((l) => getId(l.stockId))).filter(Boolean)
		)
	);
	const bundles = {};
	await Promise.all(
		stockIds.map(async (sid) => {
			const b = await loadStockBundle(sid);
			if (b) b.attrDefs = await fetchAttrDefs(getId(b.product.id));
			bundles[sid] = b;
		})
	);

	const batchIds = Array.from(
		new Set(
			flat.flatMap((p) => p.lines.filter((l) => !l.manual && l.batchId).map((l) => getId(l.batchId))).filter(Boolean)
		)
	);
	const batches = {};
	await Promise.all(
		batchIds.map(async (bid) => {
			batches[bid] = await new Promise((res) => remote.batches.find(bid, { onSuccess: res }));
		})
	);

	const unitRes = await new Promise((res) => remote.structures.getUnits({ onSuccess: res }));
	const unitOptions = unitRes.items || [];

	const items = await Promise.all(
		flat.map(async (p) => {
			const stockLines = p.lines.filter((l) => !l.manual && l.stockId);
			let formattedProduct = "";
			const firstLine = stockLines[0];
			if (firstLine) {
				const b = bundles[getId(firstLine.stockId)];
				const base = b ? formatProductName(b, lang, { showDimensions: false }) : "";
				const attrs = b
					? getAttributes(vm, b.stock.product?.attributes || b.stock.attributes, b.attrDefs)
							.split("\n")
							.join(" – ")
					: "";
				formattedProduct = attrs ? `${base} – ${attrs}` : base;
			} else {
				formattedProduct = p.name[lang] || p.name._default || "";
			}

			const rawDims = await Promise.all(
				p.lines.map((line) => formatLineDimensions(line, p, bundles, batches, unitOptions))
			);
			const dimensions = Array.from(new Set(rawDims)).join(", ");
			const total = round(p.quantity * p.amount, 2);
			return { ...p, formattedProduct, dimensions, total, totalFormatted: nf.format(total) };
		})
	);

	const totalAmount = round(
		items.reduce((s, i) => s + i.total, 0),
		2
	);
	return { items, totalAmount, totalFormatted: nf.format(totalAmount) };
}

export async function loadProformaLines(proforma, vm) {
	const lang = vm.$lang || vm.$i18n.locale;
	const currencyCode = proforma.currency.code;
	const nf = new Intl.NumberFormat(lang, { style: "currency", currency: currencyCode });
	const products = (proforma.products || []).flat();
	const lineEnvelope = [];
	products.forEach((p) => (p.lines || []).forEach((line) => lineEnvelope.push({ p, line })));

	const stockIds = Array.from(
		new Set(lineEnvelope.filter(({ line }) => !line.manual && line.stockId).map(({ line }) => getId(line.stockId)))
	).filter(Boolean);
	const bundles = {};
	await Promise.all(
		stockIds.map(async (sid) => {
			const b = await loadStockBundle(sid);
			if (b) b.attrDefs = await fetchAttrDefs(getId(b.product.id));
			bundles[sid] = b;
		})
	);

	const batchIds = Array.from(
		new Set(lineEnvelope.filter(({ line }) => !line.manual && line.batchId).map(({ line }) => getId(line.batchId)))
	).filter(Boolean);
	const batches = {};
	await Promise.all(
		batchIds.map(async (bid) => {
			batches[bid] = await new Promise((res) => remote.batches.find(bid, { onSuccess: res }));
		})
	);

	const unitRes = await new Promise((res) => remote.structures.getUnits({ onSuccess: res }));
	const unitOptions = unitRes.items || [];

	const items = await Promise.all(
		lineEnvelope.map(async ({ p, line }) => {
			let formattedProduct = "";
			if (!line.manual && line.stockId) {
				const b = bundles[getId(line.stockId)];
				const base = b ? formatProductName(b, lang, { showDimensions: false }) : "";
				const attrs = b
					? getAttributes(vm, b.stock.product?.attributes || b.stock.attributes, b.attrDefs)
							.split("\n")
							.join(" – ")
					: "";
				formattedProduct = attrs ? `${base} – ${attrs}` : base;
			} else {
				formattedProduct = p.name?.[lang] || p.name?._default || "";
			}

			const dimensions = await formatLineDimensions(line, p, bundles, batches, unitOptions);
			const quantity = round(line.quantity || 0, 4);
			const unitCode = p.unitCode || "";
			const pieces = line.pieces || 0;
			const total = round(quantity * pieces, 4);
			const unitPrice = round(p.amount || 0, 2);
			const totalPrice = round(quantity * unitPrice, 2);

			return {
				formattedProduct,
				dimensions,
				quantity,
				unitCode,
				pieces,
				total,
				totalFormatted: nf.format(total),
				unitPrice,
				unitPriceFormatted: nf.format(unitPrice),
				totalPrice,
				totalPriceFormatted: nf.format(totalPrice),
				lines: [line],
			};
		})
	);

	const totalAmount = round(
		items.reduce((s, i) => s + i.total, 0),
		2
	);
	return { items, totalAmount, totalFormatted: nf.format(totalAmount) };
}
